/**
 * How the entity-resolution UI words an audited merge, by the reason the backend recorded.
 *
 * `mergeReasonBadge` gives the audit table's short label, a key of
 * `cases.investigate.entityResolution.audit.reasonBadges`; a geocoded Address fold
 * (ADR-0034) is checked before the generic `auto` prefix so it keeps its own badge,
 * and two identities an analyst merged on a face match (ADR-0042), on a confirmed tattoo
 * pair (ADR-0049, reason `tattoo_match`) or on a confirmed pair of voiceprints (reason
 * `voice_match`) get their own.
 * `mergeReasonSentence` gives the full wording the merged-entity banner and the audit
 * drawer show. A reason with no badge or translation reads as its own words.
 */
import type { ComponentProps } from 'react'
import type { Badge } from '@/components/ui/badge'

export type MergeReasonBadge =
  | 'manual'
  | 'canonical'
  | 'geocoded'
  | 'face'
  | 'tattoo'
  | 'voice'
  | 'auto'
  | 'fuzzy'
type BadgeVariant = NonNullable<ComponentProps<typeof Badge>['variant']>
/** The slice of i18next's `t` a sentence needs. */
type Translate = (key: string, options: { defaultValue: string }) => string

export function mergeReasonBadge(reason: string): {
  badge: MergeReasonBadge | null
  tone: BadgeVariant
} {
  if (reason === 'manual') return { badge: 'manual', tone: 'info' }
  if (reason === 'set_canonical') return { badge: 'canonical', tone: 'muted' }
  if (reason === 'auto_geocoded_address') return { badge: 'geocoded', tone: 'success' }
  if (reason === 'face_match') return { badge: 'face', tone: 'info' }
  if (reason === 'tattoo_match') return { badge: 'tattoo', tone: 'info' }
  if (reason === 'voice_match') return { badge: 'voice', tone: 'info' }
  if (reason.startsWith('auto')) return { badge: 'auto', tone: 'success' }
  if (reason.startsWith('fuzzy')) return { badge: 'fuzzy', tone: 'warning' }
  return { badge: null, tone: 'muted' }
}

/** A reason's own words: `auto_same_document` reads "auto same document". */
export function mergeReasonText(reason: string): string {
  return reason.replace(/_/g, ' ')
}

export function mergeReasonSentence(t: Translate, reason: string): string {
  return t(`investigations.entityDetail.merged.reasons.${reason}`, {
    defaultValue: mergeReasonText(reason),
  })
}
